import {ApiError} from 'client/core/ApiError';
import type {RoadmapAuditFindingsResponse} from 'client/models/RoadmapAuditFindingsResponse';
import {RoadmapService} from 'client/services/RoadmapService';
import {parseAndExtractTextFromLexicalJson} from 'components/DSYS/editor/utils/lexical';
import {getService} from 'utilities';

import type {
  RoadmapAncestryItem,
  RoadmapIssueType,
  RoadmapItem,
  RoadmapLink,
  RoadmapWarnings,
} from './roadmapData';

export interface RoadmapApiMeta {
  last_successful_sync_at?: string | null;
  last_cursor?: string | null;
  last_error?: string | null;
  total_items?: number | null;
  sync_duration_ms?: number | null;
  full_rebuild?: boolean | null;
}

interface BackendRoadmapItem {
  jira_id?: string | null;
  jira_key: string;
  title: string;
  description?: string | null;
  status?: string | null;
  lifecycle?: string | null;
  issue_type: string;
  start_date?: string | null;
  end_date?: string | null;
  delivery_quarter?: string | null;
  planning_quarter?: string | null;
  jira_updated_at?: string | null;
  parent_key?: string | null;
  parent_title?: string | null;
  ancestry?: string[] | null;
  ancestor_keys?: string[] | null;
  narrative_key?: string | null;
  narrative_title?: string | null;
  sub_workstream_key?: string | null;
  sub_workstream_title?: string | null;
  assignee_name?: string | null;
  assignee_email?: string | null;
  employee_id?: string | null;
  workstream_id?: number | null;
  workstream_archived?: boolean | null;
  workstream_order?: number | null;
  latest_workstream_update?: {
    comment?: string | null;
    status?: string | null;
    created_at?: string | null;
  } | null;
  investment_category?: string | null;
  total_est_hc?: number | null;
  dev_estimate_q1?: number | null;
  dev_estimate_q2?: number | null;
  dev_estimate_q3?: number | null;
  dev_estimate_q4?: number | null;
  prd_url?: string | null;
  ux_design_url?: string | null;
  tech_spec_url?: string | null;
  confluence_links?: Array<
    string | {label?: string; title?: string; url?: string; href?: string}
  > | null;
  warnings?: {
    past_start_date?: boolean;
    missing_end_date?: boolean;
    past_end_date?: boolean;
  } | null;
}

export interface RoadmapLaunchesResponse {
  recent: BackendRoadmapItem[];
  upcoming: BackendRoadmapItem[];
  overdue: BackendRoadmapItem[];
  cancelled?: BackendRoadmapItem[];
  meta?: RoadmapApiMeta | null;
}

export interface RoadmapResponse {
  progress?: {
    done?: number;
    in_progress?: number;
    not_started?: number;
    total?: number;
  } | null;
  items: BackendRoadmapItem[];
  meta?: RoadmapApiMeta | null;
}

export interface RoadmapAllocationsResponse {
  workstreams: Array<{
    jira_key: string;
    title: string;
    initiative_count: number;
    total_headcount: number | null;
    /** Selected-quarter development weeks grouped by investment category. */
    investment_categories: Record<string, number> | null;
    dev_estimates: {
      q1?: number | null;
      q2?: number | null;
      q3?: number | null;
      q4?: number | null;
    } | null;
  }>;
  totals: {
    initiative_count: number;
    total_headcount: number | null;
    /** Selected-quarter development weeks grouped by investment category. */
    investment_categories: Record<string, number> | null;
  };
  meta?: RoadmapApiMeta | null;
}

const issueTypeMap: Record<string, RoadmapIssueType> = {
  company_narrative: 'Company Narrative',
  narrative_workstream: 'Narrative Workstream',
  sub_workstream: 'Sub-Workstream',
  roadmap_initiative: 'Roadmap Initiative',
};

const normalizeNullableString = (value: string | null | undefined) => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
};

const normalizeNullableNumber = (value: number | null | undefined): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

const normalizeIssueType = (issueType: string): RoadmapIssueType | null =>
  issueTypeMap[issueType.trim().toLowerCase()] ?? null;

const normalizeWarnings = (
  warnings: BackendRoadmapItem['warnings'] | null | undefined
): RoadmapWarnings => ({
  pastStartDate: Boolean(warnings?.past_start_date),
  missingEndDate: Boolean(warnings?.missing_end_date),
  pastEndDate: Boolean(warnings?.past_end_date),
});

const normalizeLatestWorkstreamUpdate = (
  update: BackendRoadmapItem['latest_workstream_update'] | null | undefined
): RoadmapItem['latestWorkstreamUpdate'] => {
  const source = normalizeNullableString(update?.comment);
  const status = normalizeNullableString(update?.status);
  const createdAt = normalizeNullableString(update?.created_at);
  const text = source ? parseAndExtractTextFromLexicalJson(source).trim() : null;

  return source && text && status && createdAt ? {source, status, createdAt} : null;
};

const buildConfluenceLinks = (
  entries: BackendRoadmapItem['confluence_links'] | null | undefined
): RoadmapLink[] => {
  if (!entries) return [];

  return entries.flatMap((entry, index) => {
    if (typeof entry === 'string') {
      return normalizeNullableString(entry) ? [{label: `Doc ${index + 1}`, url: entry}] : [];
    }

    const url = normalizeNullableString(entry.url ?? entry.href ?? null);
    if (!url) return [];

    return [
      {
        label: normalizeNullableString(entry.label ?? entry.title ?? null) ?? `Doc ${index + 1}`,
        url,
      },
    ];
  });
};

const buildRoadmapLinks = (item: BackendRoadmapItem): RoadmapLink[] => {
  const links: RoadmapLink[] = [];

  if (normalizeNullableString(item.prd_url)) {
    links.push({label: 'PRD', url: item.prd_url as string});
  }
  if (normalizeNullableString(item.ux_design_url)) {
    links.push({label: 'UX', url: item.ux_design_url as string});
  }
  if (normalizeNullableString(item.tech_spec_url)) {
    links.push({label: 'Tech spec', url: item.tech_spec_url as string});
  }

  return [...links, ...buildConfluenceLinks(item.confluence_links)];
};

const buildAncestry = (
  ancestorKeys: string[] | null | undefined,
  ancestryTitles: string[] | null | undefined
): RoadmapAncestryItem[] => {
  if (!ancestorKeys || ancestorKeys.length === 0) {
    return [];
  }

  return ancestorKeys.map((key, index) => ({
    key,
    title: ancestryTitles?.[index] ?? key,
  }));
};

export const buildRoadmapItemFromApi = (item: BackendRoadmapItem): RoadmapItem | null => {
  const issueType = normalizeIssueType(item.issue_type);
  if (!issueType) return null;

  return {
    key: item.jira_key,
    title: item.title,
    description: item.description ?? '',
    status: normalizeNullableString(item.status),
    lifecycle: normalizeNullableString(item.lifecycle),
    issueType,
    parentKey: normalizeNullableString(item.parent_key),
    parentTitle: normalizeNullableString(item.parent_title),
    ancestry: buildAncestry(item.ancestor_keys, item.ancestry),
    startDate: normalizeNullableString(item.start_date),
    endDate: normalizeNullableString(item.end_date),
    deliveryQuarter: normalizeNullableString(item.delivery_quarter),
    planningQuarter: normalizeNullableString(item.planning_quarter),
    jiraUpdatedAt: normalizeNullableString(item.jira_updated_at),
    assigneeName: normalizeNullableString(item.assignee_name),
    assigneeEmail: normalizeNullableString(item.assignee_email),
    employeeId: normalizeNullableString(item.employee_id),
    workstreamId: item.workstream_id ?? null,
    workstreamArchived: item.workstream_archived ?? null,
    workstreamOrder: normalizeNullableNumber(item.workstream_order),
    latestWorkstreamUpdate: normalizeLatestWorkstreamUpdate(item.latest_workstream_update),
    investmentCategory: normalizeNullableString(item.investment_category),
    totalEstHc: item.total_est_hc ?? null,
    quarterEstimates: {
      q1: item.dev_estimate_q1 ?? null,
      q2: item.dev_estimate_q2 ?? null,
      q3: item.dev_estimate_q3 ?? null,
      q4: item.dev_estimate_q4 ?? null,
    },
    links: buildRoadmapLinks(item),
    narrativeKey: normalizeNullableString(item.narrative_key),
    narrativeTitle: normalizeNullableString(item.narrative_title),
    subWorkstreamKey: normalizeNullableString(item.sub_workstream_key),
    subWorkstreamTitle: normalizeNullableString(item.sub_workstream_title),
    ancestorKeys: item.ancestor_keys ?? [],
    warnings: normalizeWarnings(item.warnings),
  };
};

export interface RoadmapContentFilters {
  timeframe: string | null;
  scopeKey: string | null;
  quarter?: string | null;
  groupIds?: string[] | null;
}

export interface RoadmapPersonalizationFilters {
  assignee?: 'all' | 'me';
  assigneeScopeEmployeeId?: string | null;
}

export interface RoadmapDatasetFilters
  extends RoadmapContentFilters, RoadmapPersonalizationFilters {}

export interface RoadmapLaunchFilters
  extends RoadmapContentFilters, RoadmapPersonalizationFilters {}

export interface RoadmapRollupFilters extends RoadmapPersonalizationFilters {
  timeframe: string;
  quarter: string;
  scopeKey?: string | null;
  groupIds?: string[] | null;
}

export interface RoadmapAuditFindingsFilters {
  timeframe: string;
  quarter: string;
  scopeKey?: string | null;
  groupIds?: string[] | null;
  assignee?: 'all' | 'me';
  assigneeKeys?: string[];
  assigneeScopeEmployeeId?: string | null;
  ruleIds?: string[];
  ruleMode?: 'any' | 'all';
  statuses?: string[];
  query?: string | null;
  cursor?: string | null;
  limit?: number;
}

const generatedFilterArguments = (params: RoadmapContentFilters) =>
  [
    undefined,
    params?.quarter ?? undefined,
    params?.timeframe ?? undefined,
    params?.scopeKey ?? undefined,
    params?.groupIds?.length ? params.groupIds : undefined,
  ] as const;

const requestRoadmapContent = async <T>(request: () => Promise<T>): Promise<T> => {
  try {
    return await request();
  } catch (error) {
    if (!(error instanceof ApiError)) throw error;
    const detail = error.body?.detail;
    const message =
      (typeof detail === 'object' && typeof detail?.message === 'string'
        ? detail.message
        : typeof detail === 'string'
          ? detail
          : null) ?? error.statusText;
    throw new Error(`Failed to load roadmap data (${error.status}): ${message}`, {
      cause: error,
    });
  }
};

export const fetchRoadmapFacets = () =>
  getService(RoadmapService).getRoadmapFacetsApiV1RoadmapFacetsGet();

export const fetchRoadmapPersonScope = (employeeId: string) =>
  getService(RoadmapService).getRoadmapPersonScopeApiV1RoadmapPeopleEmployeeIdScopeGet(employeeId);

export const fetchRoadmapNavigation = (timeframe?: string | null) =>
  getService(RoadmapService).getRoadmapNavigationApiV1RoadmapNavigationGet(timeframe);

export const getRoadmapRollupQuarterKey = (quarter: string): string => {
  const match = quarter.trim().match(/^(\d{4})[\s-]?q([1-4])$/i);
  return match ? `${match[1]}-q${match[2]}` : quarter;
};

export const fetchRoadmapRollup = (params: RoadmapRollupFilters) =>
  requestRoadmapContent(() =>
    getService(RoadmapService).getRoadmapRollupApiV1RoadmapRollupGet(
      params.timeframe,
      getRoadmapRollupQuarterKey(params.quarter),
      params.scopeKey,
      params.groupIds?.length ? params.groupIds : undefined,
      params.assignee ?? 'all',
      params.assigneeScopeEmployeeId ?? undefined
    )
  );

export const fetchRoadmapLaunches = async (params: RoadmapLaunchFilters) => {
  const response = await requestRoadmapContent(
    () =>
      getService(RoadmapService).getLaunchesApiV1RoadmapLaunchesGet(
        ...generatedFilterArguments(params),
        params.assignee ?? 'all',
        params.assigneeScopeEmployeeId ?? undefined
      ) as Promise<RoadmapLaunchesResponse>
  );

  return {
    ...response,
    recent: response.recent
      .map(buildRoadmapItemFromApi)
      .filter((item): item is RoadmapItem => Boolean(item)),
    upcoming: response.upcoming
      .map(buildRoadmapItemFromApi)
      .filter((item): item is RoadmapItem => Boolean(item)),
    overdue: response.overdue
      .map(buildRoadmapItemFromApi)
      .filter((item): item is RoadmapItem => Boolean(item)),
    cancelled: (response.cancelled ?? [])
      .map(buildRoadmapItemFromApi)
      .filter((item): item is RoadmapItem => Boolean(item)),
  };
};

export const fetchRoadmapData = async (params: RoadmapDatasetFilters) => {
  const response = await requestRoadmapContent(
    () =>
      getService(RoadmapService).getRoadmapApiV1RoadmapRoadmapGet(
        ...generatedFilterArguments(params),
        params.assignee ?? 'all',
        params.assigneeScopeEmployeeId ?? undefined
      ) as Promise<RoadmapResponse>
  );

  return {
    ...response,
    items: response.items
      .map(buildRoadmapItemFromApi)
      .filter((item): item is RoadmapItem => Boolean(item)),
  };
};

export const fetchRoadmapAllocations = async (params: RoadmapContentFilters) =>
  requestRoadmapContent(
    () =>
      getService(RoadmapService).getAllocationsApiV1RoadmapAllocationsGet(
        ...generatedFilterArguments(params)
      ) as Promise<RoadmapAllocationsResponse>
  );

export const fetchRoadmapAuditFindings = async (
  params: RoadmapAuditFindingsFilters
): Promise<RoadmapAuditFindingsResponse> =>
  requestRoadmapContent(() =>
    getService(RoadmapService).getRoadmapAuditFindingsApiV1RoadmapAuditFindingsGet(
      params.timeframe,
      getRoadmapRollupQuarterKey(params.quarter),
      params.scopeKey,
      params.assignee ?? 'all',
      params.assigneeKeys,
      params.assigneeScopeEmployeeId,
      params.ruleIds,
      params.ruleMode ?? 'any',
      params.statuses,
      params.query,
      params.cursor,
      params.limit ?? 50,
      params.groupIds?.length ? params.groupIds : undefined
    )
  );
